import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import Toggle from "@shared/v2/Toggle";
import { CheckSolidV6, MinusSolidV6 } from "@shared/v2/Icomoon";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import { useCallRecordingSettings, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import AIRecordingTip from "./AIRecordingTip";

const reminderStyles = (status) =>
  !status
    ? "tw-border-[#96B7FA] tw-text-[#2454B2] tw-bg-[#F7F9FC]"
    : "tw-border-[#46C771] tw-text-[#177335] tw-bg-[#EDFAF1]";

function getCallStatusMessage(callStatus, agentCallStatus) {
  const statusMap = {
    null: {
      status: agentCallStatus === "In-progress" ? "Ready to Dial" : "Calling you at your phone",
      circleColor: "tw-bg-[#3270FA]",
    },
    Initiated: { status: "Ringing", circleColor: "tw-bg-[#3270FA]" },
    Ringing: { status: "Ringing", circleColor: "tw-bg-[#3270FA]" },
    "In-progress": { status: "Connected", circleColor: "tw-bg-green-100" },
  };

  return statusMap[callStatus] || { status: "Call Ended", circleColor: "tw-bg-[#E5342E]" };
}

const CallStatusBar = ({
  agentCallStatus = null,
  callStatus = null,
  currentCallTime = "",
  isCompleted = false,
  recorded = false,
  recordToggleDisabled = false,
  onToggleChange,
  agentPhoneNumber = null,
  statusClassName = "",
}) => {
  const RecordConfirmationIcon = recorded ? CheckSolidV6 : MinusSolidV6;
  const featureFlag = useFeatureFlags();
  const callRecordingSettings = useCallRecordingSettings();
  const aiCallSummaryEnabled = callRecordingSettings?.aiCallSummary ?? true;
  const standardCallStatus = getCallStatusMessage(callStatus, agentCallStatus);
  const toggleRef = useRef(null);
  const [recordingTipOpen, setRecordingTipOpen] = useState(false);

  useEffect(() => {
    if (callStatus === "In-progress" && aiCallSummaryEnabled) {
      setRecordingTipOpen(true);
    } else {
      setRecordingTipOpen(false);
    }
  }, [callStatus, aiCallSummaryEnabled]);

  return (
    <div className="tw-block">
      <div
        className={`tw-py-[8px] tw-px-[16px] tw-border-[1px] tw-border-solid tw-border-gray-30 tw-flex tw-items-center tw-text-gray-75 tw-bg-gray-5 tw-text-12d tw-font-semibold tw-rounded-lg ${
          !isCompleted && agentCallStatus !== "In-progress" ? "tw-rounded-bl-none tw-rounded-br-none" : ""
        } ${statusClassName}`}
      >
        <div className={`tw-w-[8px] tw-h-[8px] tw-rounded-full tw-mr-[8px] ${standardCallStatus.circleColor}`} />
        <span className="tw-leading-[20px]">{standardCallStatus.status}</span>
        <span className="tw-font-normal tw-ml-auto">
          {callStatus === "In-progress" ? currentCallTime : ""}
        </span>
        {featureFlag.callRecording && (
          <>
            <span
              className={`tw-leading-[20px] tw-font-semibold tw-text-gray-75 tw-ml-[32px] tw-whitespace-nowrap ${
                isCompleted ? "" : "tw-w-[65px]"
              }`}
            >
              {(() => {
                if (isCompleted) {
                  return recorded ? "Recorded" : "Not Recorded";
                }
                return `Record ${recorded ? "ON" : "OFF"}`;
              })()}
            </span>
            {callStatus === "In-progress" && (
              <span ref={toggleRef}>
                <Toggle
                  id="recordedToggle"
                  name="recordedToggle"
                  labelClassName="tw-ml-[8px] !tw-mb-[0px]"
                  value={recorded}
                  checked={recorded === true}
                  disabled={recordToggleDisabled}
                  onChange={() => {
                    setRecordingTipOpen(false);
                    onToggleChange();
                  }}
                  showFlash
                />
              </span>
            )}
            {recordingTipOpen && aiCallSummaryEnabled && !recordToggleDisabled && !recorded && (
              <AIRecordingTip anchorEl={toggleRef.current} onDismiss={() => setRecordingTipOpen(false)} />
            )}
            {isCompleted && <RecordConfirmationIcon className="tw-ml-[8px]" />}
          </>
        )}
      </div>
      {agentCallStatus !== "In-progress" && (
        <div
          className={`tw-py-[10px] tw-px-[16px] tw-border-[1px] tw-border-solid tw-flex tw-items-center tw-text-12d tw-font-semibold tw-rounded-lg tw-rounded-tl-none tw-rounded-tr-none ${reminderStyles(
            callStatus,
          )} ${isCompleted || (callStatus && !featureFlag.callRecording) ? "tw-hidden" : ""}`}
        >
          {!callStatus
            ? `To start the call, please answer your phone ${
                agentPhoneNumber ? `at ${formatPhoneNumber(agentPhoneNumber)}` : ""
              }`
            : "Please inform the other party before recording."}
        </div>
      )}
    </div>
  );
};

export default CallStatusBar;

CallStatusBar.propTypes = {
  agentCallStatus: PropTypes.string,
  callStatus: PropTypes.string,
  currentCallTime: PropTypes.string,
  isCompleted: PropTypes.bool,
  recorded: PropTypes.bool,
  recordToggleDisabled: PropTypes.bool,
  onToggleChange: PropTypes.func.isRequired,
  agentPhoneNumber: PropTypes.string,
  statusClassName: PropTypes.string,
};
